import { useCallback, useEffect, useMemo, useState } from "react";
import { FormInstance, SelectOption } from "@ir/ui";
import { useLookup, useWorkspace } from "@/hooks";
import { Deduction } from "@/core/entities/deduction";
import { useDeductionEdit } from "@/presentation/deduction/hooks/deduction";

const EMPTY_ARRAY: any[] = [];

interface UseDeductionDetailsOptionsParams {
  deduction: Deduction;
  form: FormInstance;
}

export const mapBrandMembersToOptions = (brandMembers?: { fullName: string; userId: number; isDeleted?: boolean }[]): SelectOption[] => {
  return (
    brandMembers
      ?.filter(member => !member?.isDeleted)
      ?.map(member => ({
        label: member?.fullName,
        value: member?.userId?.toString(),
      })) ?? []
  );
};

export const useDeductionDetailsOptions = ({ deduction, form }: UseDeductionDetailsOptionsParams) => {
  const { workspace } = useWorkspace();
  const { getBrandMembersLookup, brandMembersLookup } = useLookup();

  const {
    deductionTypes,
    getDeductionTypes,
    distributionCenters,
    getDistributionCenters,
    glCodes,
    getGLCodes,
    retailers,
    getRetailers,
  } = useDeductionEdit();

  const workspaceKey = workspace?.workspaceKey;

  useEffect(() => {
    if (!workspaceKey) {
      return;
    }
    getBrandMembersLookup({  include_deleted: true, include_bots: false, role_key: [] });
    getDeductionTypes();
    getDistributionCenters();
    getGLCodes();
    getRetailers();
  }, [workspaceKey, getBrandMembersLookup, getDeductionTypes, getDistributionCenters, getGLCodes, getRetailers]);

  const brandMembersData = brandMembersLookup?.data?.filter(member => !member?.isDeleted) ?? EMPTY_ARRAY;
  const deductionTypesData = deductionTypes?.data ?? EMPTY_ARRAY;
  const distributionCentersData = distributionCenters?.data ?? EMPTY_ARRAY;
  const glCodesData = glCodes?.data ?? EMPTY_ARRAY;
  const retailersData = retailers?.data ?? EMPTY_ARRAY;

  const analystOptions = useMemo(() => mapBrandMembersToOptions(brandMembersData), [brandMembersData]);
  const approverOptions = analystOptions;

  const deductionTypeOptions = useMemo(() => {
    if (!deductionTypesData.length) {
      return [];
    }
    return deductionTypesData
      .filter(deductionType => deductionType.parentId === null)
      .map(deductionType => ({
        label: deductionType?.deductionName,
        value: deductionType?.id.toString(),
      }));
  }, [deductionTypesData]);

  const buildDeductionSubTypeOptions = useCallback(
    (parentId?: string | null): SelectOption[] => {
      if (!deductionTypesData.length) {
        return [];
      }
      return deductionTypesData
        .filter(deductionType => deductionType.parentId !== null && (parentId ? deductionType.parentId?.toString() === parentId : true))
        .map(deductionType => ({
          label: deductionType.deductionName,
          value: deductionType.id.toString(),
        }));
    },
    [deductionTypesData]
  );

  const [deductionSubTypeOptions, setDeductionSubTypeOptions] = useState<SelectOption[]>(() =>
    buildDeductionSubTypeOptions(deduction?.deductionTypeId?.toString())
  );

  const syncDeductionSubTypeOptions = useCallback(
    (parentId?: string | null) => {
      const resolvedParentId = parentId ?? deduction?.deductionTypeId?.toString() ?? null;
      setDeductionSubTypeOptions(buildDeductionSubTypeOptions(resolvedParentId));
      return resolvedParentId;
    },
    [buildDeductionSubTypeOptions, deduction?.deductionTypeId]
  );

  const distributionCenterOptions = useMemo(() => {
    if (!distributionCentersData.length) {
      return [];
    }
    return (
      distributionCentersData
        .filter(distributionCenter => distributionCenter?.purchaserName === deduction?.purchaser)
        .map(distributionCenter => ({
          label: `${distributionCenter?.dcName} (${distributionCenter?.dcCode})`,
          value: distributionCenter?.dcCode,
        })) ?? []
    );
  }, [distributionCentersData, deduction?.purchaser]);

  const glCodeOptions = useMemo(() => {
    if (!glCodesData.length) {
      return [];
    }
    return glCodesData?.map(glCode => ({
      label: glCode.displayName,
      value: glCode.glCodeId.toString(),
    }));
  }, [glCodesData]);

  const retailerOptions = useMemo(() => {
    if (!retailersData.length) {
      return [];
    }
    return retailersData?.map(retailer => ({
      label: retailer.retailerName as string,
      value: retailer.retailerKey as string,
    }));
  }, [retailersData]);

  const handleDeductionTypeMetaChange = useCallback(() => {
    const deductionTypeValue = form.getFieldValue("deductionType");
    const deductionSubTypeValue = form.getFieldValue("deductionSubType");
    syncDeductionSubTypeOptions(deductionTypeValue?.value ?? null);
    if (deductionTypeValue && deductionSubTypeValue?.value) {
      const subTypeParentId = deductionTypesData?.find(deductionType => deductionType.id.toString() === deductionSubTypeValue.value)?.parentId;
      if (subTypeParentId?.toString() !== deductionTypeValue.value) {
        form.setFieldValue("deductionSubType", null);
      }
    }
  }, [deductionTypesData, form, syncDeductionSubTypeOptions]);

  const handleDeductionSubTypeMetaChange = useCallback(() => {
    const deductionSubTypeValue = form.getFieldValue("deductionSubType");
    const deductionTypeValue = form.getFieldValue("deductionType");
    if (deductionSubTypeValue?.value && !deductionTypeValue) {
      const deductionSubType = deductionTypesData?.find(deductionType => deductionType.id.toString() === deductionSubTypeValue.value);
      if (deductionSubType?.parentId) {
        const deductionTypeParent = deductionTypesData?.find(deductionType => deductionType.id.toString() === deductionSubType.parentId?.toString());
        if (deductionTypeParent) {
          form.setFieldValue("deductionType", {
            label: deductionTypeParent.deductionName,
            value: deductionTypeParent.id.toString(),
          });
        }
      }
    }
  }, [deductionTypesData, form]);

  return {
    analystOptions,
    approverOptions,
    deductionTypeOptions,
    deductionSubTypeOptions,
    distributionCenterOptions,
    glCodeOptions,
    retailerOptions,
    handleDeductionTypeMetaChange,
    handleDeductionSubTypeMetaChange,
    syncDeductionSubTypeOptions,
    brandMembers: brandMembersData,
    isBrandMembersLoading: brandMembersLookup.isLoading,
    isDeductionTypesLoading: deductionTypes.isLoading,
    isDistributionCentersLoading: distributionCenters.isLoading,
    isGlCodesLoading: glCodes.isLoading,
    isRetailersLoading: retailers.isLoading,
  };
};


